// ui/inventoryUI.js
// Inventory grid UI component (4x7 = 28 slots)

import { gameState } from '../game/gameState.js';
import { SKILL_CATEGORIES } from '../game/constants.js';
import { getItem, getItemName, getItemIcon, getItemColor } from '../data/items/index.js';
import { formatNumber } from '../utils/format.js';
import { toastSuccess, toastWarning, toastError } from './toast.js';
import logger from '../utils/logger.js';
import { combatSkill } from '../skills/combat/combat.js';
import { showInsightModal, isInsightItem } from './insightModal.js';
import { updatePinnedQuests } from './pinnedQuestsUI.js';
import { buildPotionEffectsText, buildFoodEffectsText, getEffectiveFoodHeal } from './equipmentTooltip.js';
import { SET_BONUSES } from '../data/items/setBonusData.js';
import { SET_INFO } from '../data/items/setArmor.js';

// Tier colors for equipment UI styling (border glow, name color)
// These are for tier indication, NOT material color matching
const TIER_COLORS = {
    tier1: '#CD853F',   // Tier 1 - peru/tan
    tier2: '#3CB371',   // Tier 2 - medium sea green
    tier3: '#5B9BD5',   // Tier 3 - royal blue
    tier4: '#E85D75',   // Tier 4 - crimson
    tier5: '#A8A8A8',   // Tier 5 - silver
    tier6: '#B088F9'    // Tier 6 - purple
};

/**
 * Get tier color based on combat level requirement
 */
function getTierColor(combatLevel) {
    if (!combatLevel || combatLevel < 15) return TIER_COLORS.tier1;
    if (combatLevel < 30) return TIER_COLORS.tier2;
    if (combatLevel < 45) return TIER_COLORS.tier3;
    if (combatLevel < 60) return TIER_COLORS.tier4;
    if (combatLevel < 75) return TIER_COLORS.tier5;
    return TIER_COLORS.tier6;
}

/**
 * Format a stat value with comparison coloring
 * @param {number} itemValue - Value on the item being compared
 * @param {number} equippedValue - Value on currently equipped item
 * @param {string} statLabel - Label for the stat
 * @param {boolean} showComparison - Whether to show comparison colors
 * @param {boolean} lowerIsBetter - For attack speed, lower is better
 * @returns {string} Formatted stat line with color
 */
function formatStatComparison(itemValue, equippedValue, statLabel, showComparison, lowerIsBetter = false) {
    const diff = itemValue - equippedValue;

    if (!showComparison || diff === 0) {
        // No comparison or same value
        return `+${itemValue} ${statLabel}`;
    }

    // Determine if this is better or worse
    const isBetter = lowerIsBetter ? diff < 0 : diff > 0;
    const color = isBetter ? '#4CAF50' : '#f44336'; // green or red
    const sign = diff > 0 ? '+' : '';

    return `<span style='color: ${color};'>+${itemValue} ${statLabel} (${sign}${diff})</span>`;
}

/**
 * Determine which equipment set an item belongs to based on its style
 * @param {Object} item - The item to check
 * @returns {string|null} 'melee', 'ranged', 'magic', or null if universal
 */
function getItemCombatStyle(item) {
    if (!item) return null;

    // Check attackStyle for weapons
    if (item.attackStyle) {
        return item.attackStyle.toLowerCase();
    }

    // Check armorType for armor
    if (item.armorType) {
        return item.armorType.toLowerCase();
    }

    // Check for accuracy types to infer style
    if (item.meleeAccuracy && !item.rangedAccuracy && !item.magicAccuracy) return 'melee';
    if (item.rangedAccuracy && !item.meleeAccuracy && !item.magicAccuracy) return 'ranged';
    if (item.magicAccuracy && !item.meleeAccuracy && !item.rangedAccuracy) return 'magic';

    return null; // Universal item (rings, some accessories)
}

/**
 * Build tooltip text for an item, with colored name for equipment
 */
function buildItemTooltip(item, itemId, quantity, isLocked) {
    const name = getItemName(itemId);
    const exactQty = quantity.toLocaleString();
    const lockHint = isLocked ? ' (Locked - right-click to unlock)' : '';

    // Check if item is equippable, edible, drinkable, or a usable insight
    const isEquippable = item && item.slot;
    const isEdible = item && item.healAmount;
    const isPotion = item && item.effects && item.category === 'potion';
    const isInsight = item && item.usable && item.xpAmount;

    let actionHint = '';
    if (isEquippable) actionHint = ' (Click to equip)';
    else if (isEdible) {
        const effectiveHeal = getEffectiveFoodHeal(item);
        const hotText = item.hot ? ` + ${item.hot.amount * item.hot.ticks} over ${(item.hot.duration / 1000).toFixed(0)}s` : '';
        actionHint = ` (Click to eat <span style='color: #4CAF50;'>+${effectiveHeal} HP${hotText}</span>)`;
    }
    else if (isPotion) {
        const effectsText = buildPotionEffectsText(item.effects);
        actionHint = effectsText ? `\n\n${effectsText}\n(Click to drink)` : ' (Click to drink)';
    }
    else if (isInsight) {
        const isSingleSkill = item.allowedCategories && item.allowedCategories.length === 1
            && Object.values(SKILL_CATEGORIES).some(c => c.id === item.allowedCategories[0] && c.skills.length === 1);
        actionHint = isSingleSkill ? ' (Click to use, Shift+Click to use all)' : ' (Click to use)';
    }

    // For equipment, use colored name with stats
    if (isEquippable && item) {
        const tierColor = getTierColor(item.combatLevel);
        const coloredName = `<span style='color: ${tierColor}; font-weight: bold;'>${name}</span>`;

        // Check if stat comparison is enabled
        const showComparison = gameState.settings.showStatComparison !== false;

        // Determine which set to compare against based on item's combat style
        let equippedItem = null;
        if (showComparison) {
            const itemStyle = getItemCombatStyle(item);
            const sets = gameState.getEquipmentSets();

            // Get the appropriate set's gear based on item style
            let compareSet = itemStyle || gameState.getActiveEquipmentSet();
            if (compareSet && sets[compareSet]) {
                const equippedItemId = sets[compareSet].gear[item.slot];
                if (equippedItemId && equippedItemId !== itemId) {
                    equippedItem = getItem(equippedItemId);
                }
            }
        }

        // Compare even when slot is empty — going from nothing to something is an upgrade
        const doCompare = showComparison && item.slot;

        // Build stat list with comparisons
        const stats = [];

        // For magic weapons (staffs), show spell damage multiplier instead of attack
        if (item.magicDamage) {
            const equipped = equippedItem?.magicDamage || 0;
            const diff = item.magicDamage - equipped;
            const isBetter = diff > 0;
            const color = doCompare ? (isBetter ? '#4CAF50' : (diff < 0 ? '#f44336' : '')) : '';
            const diffText = doCompare && diff !== 0 ? ` (${diff > 0 ? '+' : ''}${diff.toFixed(1)})` : '';
            if (color) {
                stats.push(`<span style='color: ${color};'>${item.magicDamage.toFixed(1)}x Spell Dmg${diffText}</span>`);
            } else {
                stats.push(`${item.magicDamage.toFixed(1)}x Spell Dmg`);
            }
        } else if (item.attackBonus) {
            const equipped = equippedItem?.attackBonus || 0;
            stats.push(formatStatComparison(item.attackBonus, equipped, 'Attack', doCompare));
        }
        if (item.defenseBonus) {
            const equipped = equippedItem?.defenseBonus || 0;
            stats.push(formatStatComparison(item.defenseBonus, equipped, 'Defense', doCompare));
        }
        // Show spell damage bonus from magic armor
        if (item.spellDamage) {
            const equipped = equippedItem?.spellDamage || 0;
            const itemPct = Math.round(item.spellDamage * 100);
            const equippedPct = Math.round(equipped * 100);
            const diff = itemPct - equippedPct;
            const isBetter = diff > 0;
            const color = doCompare ? (isBetter ? '#4CAF50' : (diff < 0 ? '#f44336' : '')) : '';
            const diffText = doCompare && diff !== 0 ? ` (${diff > 0 ? '+' : ''}${diff}%)` : '';
            if (color) {
                stats.push(`<span style='color: ${color};'>+${itemPct}% Spell Dmg${diffText}</span>`);
            } else {
                stats.push(`+${itemPct}% Spell Dmg`);
            }
        }

        // Show all accuracy stats with comparison
        if (item.meleeAccuracy) {
            const equipped = equippedItem?.meleeAccuracy || 0;
            stats.push(formatStatComparison(item.meleeAccuracy, equipped, 'Melee Acc', doCompare));
        }
        if (item.rangedAccuracy) {
            const equipped = equippedItem?.rangedAccuracy || 0;
            stats.push(formatStatComparison(item.rangedAccuracy, equipped, 'Ranged Acc', doCompare));
        }
        if (item.magicAccuracy) {
            const equipped = equippedItem?.magicAccuracy || 0;
            stats.push(formatStatComparison(item.magicAccuracy, equipped, 'Magic Acc', doCompare));
        }

        if (item.attackSpeed) {
            if (showComparison && equippedItem?.attackSpeed) {
                // Lower attack speed is better (faster attacks)
                const diff = item.attackSpeed - equippedItem.attackSpeed;
                const isBetter = diff < 0;
                const color = isBetter ? '#4CAF50' : (diff > 0 ? '#f44336' : '');
                const diffText = diff !== 0 ? ` (${diff > 0 ? '+' : ''}${(diff / 1000).toFixed(1)}s)` : '';
                if (color) {
                    stats.push(`<span style='color: ${color};'>${(item.attackSpeed / 1000).toFixed(1)}s Speed${diffText}</span>`);
                } else {
                    stats.push(`${(item.attackSpeed / 1000).toFixed(1)}s Speed`);
                }
            } else {
                stats.push(`${(item.attackSpeed / 1000).toFixed(1)}s Speed`);
            }
        }
        // Jewelry stats
        if (item.speedBonus) {
            const equipped = equippedItem?.speedBonus || 0;
            const diff = item.speedBonus - equipped;
            const isBetter = diff > 0;
            const color = doCompare ? (isBetter ? '#4CAF50' : (diff < 0 ? '#f44336' : '#888')) : '';
            const diffText = doCompare && diff !== 0 ? ` (${diff > 0 ? '+' : ''}${diff}%)` : '';
            if (color) {
                stats.push(`<span style='color: ${color};'>+${item.speedBonus}% Atk Speed${diffText}</span>`);
            } else {
                stats.push(`+${item.speedBonus}% Atk Speed`);
            }
        }
        if (item.critChance) {
            const equipped = equippedItem?.critChance || 0;
            const diff = item.critChance - equipped;
            const isBetter = diff > 0;
            const color = doCompare ? (isBetter ? '#4CAF50' : (diff < 0 ? '#f44336' : '#888')) : '';
            const diffText = doCompare && diff !== 0 ? ` (${diff > 0 ? '+' : ''}${diff}%)` : '';
            if (color) {
                stats.push(`<span style='color: ${color};'>+${item.critChance}% Crit${diffText}</span>`);
            } else {
                stats.push(`+${item.critChance}% Crit`);
            }
        }
        if (item.accuracy) {
            const equipped = equippedItem?.accuracy || 0;
            stats.push(formatStatComparison(item.accuracy, equipped, 'Accuracy', doCompare));
        }
        // Bulwark/shield stats
        if (item.hpBonus) {
            const equipped = equippedItem?.hpBonus || 0;
            stats.push(formatStatComparison(item.hpBonus, equipped, 'Max HP', doCompare));
        }
        if (item.blockChance) {
            const equipped = equippedItem?.blockChance || 0;
            const diff = item.blockChance - equipped;
            const isBetter = diff > 0;
            const color = doCompare ? (isBetter ? '#4CAF50' : (diff < 0 ? '#f44336' : '#888')) : '';
            const diffText = doCompare && diff !== 0 ? ` (${diff > 0 ? '+' : ''}${diff}%)` : '';
            if (color) {
                stats.push(`<span style='color: ${color};'>${item.blockChance}% Block${diffText}</span>`);
            } else {
                stats.push(`${item.blockChance}% Block`);
            }
        }
        // Quiver stats
        if (item.ammoEfficiency) {
            const equipped = equippedItem?.ammoEfficiency || 0;
            const diff = item.ammoEfficiency - equipped;
            const isBetter = diff > 0;
            const color = doCompare ? (isBetter ? '#4CAF50' : (diff < 0 ? '#f44336' : '#888')) : '';
            const diffText = doCompare && diff !== 0 ? ` (${diff > 0 ? '+' : ''}${diff}%)` : '';
            if (color) {
                stats.push(`<span style='color: ${color};'>${item.ammoEfficiency}% Ammo Eff${diffText}</span>`);
            } else {
                stats.push(`${item.ammoEfficiency}% Ammo Eff`);
            }
        }
        // Ranged damage bonus
        if (item.rangedDamage) {
            const equipped = equippedItem?.rangedDamage || 0;
            stats.push(formatStatComparison(item.rangedDamage, equipped, 'Ranged Dmg', doCompare));
        }

        // Show equipment slot with friendly name
        if (item.slot) {
            const slotNames = {
                weapon: 'Weapon',
                offhand: 'Off-hand',
                head: 'Head',
                body: 'Body',
                legs: 'Legs',
                feet: 'Feet',
                hands: 'Hands',
                shoulders: 'Shoulders',
                neck: 'Neck',
                ring: 'Ring',
                ammo: 'Ammo'
            };
            let slotName = slotNames[item.slot] || item.slot;
            // Ranged hide armor's shoulders slot displays as Vambraces (forearm-guard art)
            if (item.slot === 'shoulders' && item.armorType === 'ranged') slotName = 'Vambraces';
            stats.push(`Slot: ${slotName}`);
        }

        if (item.attackStyle) stats.push(`Style: ${item.attackStyle}`);
        if (item.armorType) stats.push(`Type: ${item.armorType}`);
        // Unique/special effects
        if (item.uniqueEffect?.description) stats.push(`<span style='color: #FFD700;'>✦ ${item.uniqueEffect.description}</span>`);
        if (item.specialEffect?.description) stats.push(`<span style='color: #FFD700;'>⚔ ${item.specialEffect.description}</span>`);
        if (item.combatLevel) stats.push(`Req: Lv ${item.combatLevel}`);

        // Set armor info with full bonus details
        if (item.setId) {
            const setInfo = SET_INFO[item.setId];
            const setName = setInfo?.name || item.setId.charAt(0).toUpperCase() + item.setId.slice(1);
            const setColor = setInfo?.color || '#888';
            const state = item.awakened ? 'Awakened' : 'Dormant';
            const stateColor = item.awakened ? '#4CAF50' : '#888';

            stats.push(`<span style='color: ${setColor}; font-weight: bold;'>═══ ${setName} Set ═══</span>`);
            stats.push(`<span style='color: ${stateColor};'>Status: ${state}</span>`);

            if (!item.awakened) {
                stats.push(`<span style='color: #64b5f6; font-style: italic;'>Awaken at Shop for set bonuses</span>`);
            } else {
                // Count equipped awakened pieces of this set
                const gear = gameState.getEquippedGear();
                const armorSlots = ['head', 'shoulders', 'hands', 'body', 'legs', 'feet'];
                let equippedCount = 0;
                for (const slot of armorSlots) {
                    if (gear[slot]) {
                        const equipped = getItem(gear[slot]);
                        if (equipped?.setId === item.setId && equipped?.awakened) {
                            equippedCount++;
                        }
                    }
                }

                stats.push(`<span style='color: #aaa;'>Equipped: ${equippedCount}/6 pieces</span>`);

                // Show set bonuses with active highlighting
                const setBonusData = SET_BONUSES[item.setId];
                if (setBonusData?.bonuses) {
                    for (const threshold of [2, 4, 6]) {
                        const bonus = setBonusData.bonuses[threshold];
                        if (bonus) {
                            const isActive = equippedCount >= threshold;
                            const bonusColor = isActive ? '#4CAF50' : '#666';
                            const checkMark = isActive ? '✓ ' : '○ ';
                            stats.push(`<span style='color: ${bonusColor};'>${checkMark}(${threshold}pc) ${bonus.name}</span>`);
                            if (bonus.description) {
                                const descColor = isActive ? '#8BC34A' : '#555';
                                stats.push(`<span style='color: ${descColor}; font-size: 0.9em;'>   ${bonus.description}</span>`);
                            }
                        }
                    }
                }
            }
        }

        const statsText = stats.length > 0 ? `\n\n${stats.join('\n')}` : '';
        const actionLine = stats.length > 0 && actionHint ? `\n${actionHint.trim()}` : actionHint;
        return `${coloredName}: ${exactQty}${statsText}${lockHint}${actionLine}`;
    }

    // Include description for misc items (tokens, catalysts, etc.)
    const descText = item?.description ? `\n\n${item.description}` : '';
    return `${name}: ${exactQty}${descText}${lockHint}${actionHint}`;
}

// Constants
const INVENTORY_COLS = 4;
const INVENTORY_ROWS = 7;
const INVENTORY_SLOTS = INVENTORY_COLS * INVENTORY_ROWS; // 28

// Track previous state to avoid unnecessary DOM updates
let previousSlotState = new Array(INVENTORY_SLOTS).fill(null);
let isGridInitialized = false;

// Drag state
let dragSourceIndex = null;

/**
 * Format quantity for display in inventory slot
 * Uses compact notation for large numbers
 * @param {number} quantity - Item quantity
 * @returns {string} Formatted quantity
 */
function formatQuantity(quantity) {
    if (quantity >= 10_000_000) {
        // 10M+ -> "10M", "1.5B"
        return formatNumber(quantity);
    } else if (quantity >= 100_000) {
        // 100K+ -> "100K", "999K"
        return formatNumber(quantity);
    } else if (quantity >= 10_000) {
        // 10K+ -> "10K", "99K"
        return formatNumber(quantity);
    }
    // Under 10K -> show full number with commas
    return quantity.toLocaleString();
}

/**
 * Render a single inventory slot
 * @param {object|null} slotData - { itemId, quantity } or null for empty
 * @param {number} index - Slot index (0-27)
 * @returns {string} HTML for the slot
 */
function renderSlot(slotData, index) {
    if (!slotData) {
        // Empty slot - can be drop target (empty slots can't be locked)
        return `
            <div class="inventory-slot empty" data-slot="${index}">
                <div class="slot-content"></div>
            </div>
        `;
    }

    const { itemId, quantity } = slotData;
    const isLocked = gameState.isItemLocked(itemId);
    const lockedClass = isLocked ? 'locked' : '';
    const lockIcon = isLocked ? '<span class="lock-icon">🔒</span>' : '';

    const icon = getItemIcon(itemId, quantity);
    const color = getItemColor(itemId);
    const formattedQty = formatQuantity(quantity);

    // Check if item is equippable, edible, or usable insight
    const item = getItem(itemId);
    const isEquippable = item && item.slot;
    const isEdible = item && item.healAmount;
    const isInsight = item && item.usable && item.xpAmount;
    const equipClass = isEquippable ? 'equippable' : '';
    const edibleClass = isEdible ? 'edible' : '';
    const usableClass = isInsight ? 'usable' : '';

    // Build tooltip with colored name for equipment
    const tooltip = buildItemTooltip(item, itemId, quantity, isLocked);

    // Check for max stack warning (within 1% of MAX_SAFE_INTEGER)
    const isNearMax = quantity > Number.MAX_SAFE_INTEGER * 0.99;
    const maxWarning = isNearMax ? 'near-max' : '';

    return `
        <div class="inventory-slot occupied ${maxWarning} ${lockedClass} ${equipClass} ${edibleClass} ${usableClass}"
             data-slot="${index}"
             data-item-id="${itemId}"
             draggable="true"
             data-tooltip="${tooltip}">
            <div class="slot-content" style="background-color: ${color};">
                <span class="item-icon">${icon}</span>
                <span class="item-quantity ${isNearMax ? 'warning' : ''}">${formattedQty}</span>
            </div>
            ${lockIcon}
        </div>
    `;
}

/**
 * Render the full inventory grid
 * @returns {string} HTML for the inventory grid
 */
export function renderInventoryGrid() {
    const slots = gameState.getInventorySlots();
    const usedSlots = gameState.getUniqueItemCount();

    // Update sidebar tab badge count
    const headerCount = document.getElementById('sidebar-inventory-count');
    if (headerCount) {
        headerCount.textContent = `${usedSlots}/${INVENTORY_SLOTS}`;
    }

    let slotsHTML = '';
    for (let i = 0; i < INVENTORY_SLOTS; i++) {
        slotsHTML += renderSlot(slots[i], i);
    }

    return `
        <div class="inventory-grid">
            ${slotsHTML}
        </div>
    `;
}

/**
 * Check if two slot states are equal
 * @param {object|null} a - First slot state
 * @param {object|null} b - Second slot state
 * @returns {boolean} True if states are equal
 */
function slotsEqual(a, b) {
    if (a === null && b === null) return true;
    if (a === null || b === null) return false;
    return a.itemId === b.itemId && a.quantity === b.quantity;
}

/**
 * Update a single slot in the DOM without replacing the entire element
 * @param {HTMLElement} slotEl - The slot DOM element
 * @param {object|null} newState - New slot state { itemId, quantity } or null
 * @param {number} index - Slot index
 */
function updateSlotInPlace(slotEl, newState, index) {
    if (!newState) {
        // Slot should be empty (empty slots can't be locked)
        slotEl.className = 'inventory-slot empty';
        slotEl.removeAttribute('data-item-id');
        slotEl.removeAttribute('data-tooltip');
        slotEl.removeAttribute('draggable');
        slotEl.innerHTML = '<div class="slot-content"></div>';
        return;
    }

    const { itemId, quantity } = newState;
    const isLocked = gameState.isItemLocked(itemId);
    const lockedClass = isLocked ? ' locked' : '';
    const lockIcon = isLocked ? '<span class="lock-icon">🔒</span>' : '';

    const icon = getItemIcon(itemId, quantity);
    const color = getItemColor(itemId);
    const formattedQty = formatQuantity(quantity);
    const isNearMax = quantity > Number.MAX_SAFE_INTEGER * 0.99;

    // Check if item is equippable, edible, or usable insight
    const item = getItem(itemId);
    const isEquippable = item && item.slot;
    const isEdible = item && item.healAmount;
    const isInsight = item && item.usable && item.xpAmount;
    const equipClass = isEquippable ? ' equippable' : '';
    const edibleClass = isEdible ? ' edible' : '';
    const usableClass = isInsight ? ' usable' : '';

    // Build tooltip with colored name for equipment
    const tooltip = buildItemTooltip(item, itemId, quantity, isLocked);

    // Update classes
    slotEl.className = `inventory-slot occupied${isNearMax ? ' near-max' : ''}${lockedClass}${equipClass}${edibleClass}${usableClass}`;
    slotEl.setAttribute('data-item-id', itemId);
    slotEl.setAttribute('data-tooltip', tooltip);
    slotEl.setAttribute('draggable', 'true');

    // Update content
    slotEl.innerHTML = `
        <div class="slot-content" style="background-color: ${color};">
            <span class="item-icon">${icon}</span>
            <span class="item-quantity ${isNearMax ? 'warning' : ''}">${formattedQty}</span>
        </div>
        ${lockIcon}
    `;
}

/**
 * Update only the quantity text without replacing HTML (for performance)
 * Also updates icon if item has quantity-based sprites (coins, arrow_shafts)
 * @param {HTMLElement} slotEl - The slot DOM element
 * @param {number} quantity - New quantity
 * @param {string} itemId - Item ID for tooltip
 */
function updateQuantityOnly(slotEl, quantity, itemId) {
    const qtyEl = slotEl.querySelector('.item-quantity');
    if (qtyEl) {
        const formattedQty = formatQuantity(quantity);
        const isNearMax = quantity > Number.MAX_SAFE_INTEGER * 0.99;
        qtyEl.textContent = formattedQty;
        qtyEl.className = `item-quantity${isNearMax ? ' warning' : ''}`;

        // Update near-max class on slot
        if (isNearMax) {
            slotEl.classList.add('near-max');
        } else {
            slotEl.classList.remove('near-max');
        }

        // Update icon (may change based on quantity for coins, arrow_shafts, etc.)
        const iconEl = slotEl.querySelector('.item-icon');
        if (iconEl) {
            iconEl.innerHTML = getItemIcon(itemId, quantity);
        }

        // Update tooltip with colored name for equipment
        const item = getItem(itemId);
        const isLocked = gameState.isItemLocked(itemId);
        const tooltip = buildItemTooltip(item, itemId, quantity, isLocked);
        slotEl.setAttribute('data-tooltip', tooltip);
    }
}

/**
 * Update the inventory display in the DOM
 * Called every tick from tick.js
 * Uses incremental updates to avoid flickering
 */
export function updateInventoryDisplay() {
    const inventoryDiv = document.getElementById('inventory');
    if (!inventoryDiv) return;

    const currentSlots = gameState.getInventorySlots();
    const usedSlots = gameState.getUniqueItemCount();

    // First render - create the full grid
    if (!isGridInitialized || !inventoryDiv.querySelector('.inventory-grid')) {
        inventoryDiv.innerHTML = renderInventoryGrid();
        // Store current state
        previousSlotState = currentSlots.map(s => s ? { ...s } : null);
        isGridInitialized = true;
        return;
    }

    // Incremental update - only update changed slots
    const slotElements = inventoryDiv.querySelectorAll('.inventory-slot');

    for (let i = 0; i < INVENTORY_SLOTS; i++) {
        const prevState = previousSlotState[i];
        const newState = currentSlots[i];

        // Skip if no change
        if (slotsEqual(prevState, newState)) continue;

        const slotEl = slotElements[i];
        if (!slotEl) continue;

        // Check if only quantity changed (same item)
        if (prevState && newState &&
            prevState.itemId === newState.itemId &&
            prevState.quantity !== newState.quantity) {
            // Just update the quantity text and tooltip
            updateQuantityOnly(slotEl, newState.quantity, newState.itemId);
        } else {
            // Full slot update needed (item changed or slot became empty/occupied)
            updateSlotInPlace(slotEl, newState, i);
        }

        // Update stored state
        previousSlotState[i] = newState ? { ...newState } : null;
    }

    // Update sidebar tab badge count
    const headerCount = document.getElementById('sidebar-inventory-count');
    if (headerCount) {
        headerCount.textContent = `${usedSlots}/${INVENTORY_SLOTS}`;
    }

    // Update pinned quests (tracks item requirements)
    updatePinnedQuests();
}

/**
 * Reset inventory UI state (call when game is loaded/reset)
 */
export function resetInventoryUI() {
    previousSlotState = new Array(INVENTORY_SLOTS).fill(null);
    isGridInitialized = false;
    dragSourceIndex = null;
}

/**
 * Force refresh all inventory tooltips (call when settings change)
 */
export function forceRefreshInventory() {
    // Reset state to force full rebuild on next update
    previousSlotState = new Array(INVENTORY_SLOTS).fill(null);
    isGridInitialized = false;
    // Trigger immediate update
    updateInventoryDisplay();
}

/**
 * Handle drag start on an inventory slot
 */
function handleDragStart(e) {
    const slot = e.target.closest('.inventory-slot');
    if (!slot || slot.classList.contains('empty')) {
        e.preventDefault();
        return;
    }

    dragSourceIndex = parseInt(slot.dataset.slot, 10);
    slot.classList.add('dragging');

    // Hide tooltips during drag
    document.body.classList.add('dragging');

    // Set drag data
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', dragSourceIndex.toString());
    e.dataTransfer.setData('application/x-inventory-slot', dragSourceIndex.toString());

    logger.debug(`[INVENTORY] Drag start: slot ${dragSourceIndex}`);
}

/**
 * Handle drag over an inventory slot (enables dropping)
 */
function handleDragOver(e) {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';

    const slot = e.target.closest('.inventory-slot');
    if (slot) {
        slot.classList.add('drag-over');
    }
}

/**
 * Handle drag leave an inventory slot
 */
function handleDragLeave(e) {
    const slot = e.target.closest('.inventory-slot');
    if (slot) {
        slot.classList.remove('drag-over');
    }
}

/**
 * Handle drop on an inventory slot
 */
function handleDrop(e) {
    e.preventDefault();

    const targetSlot = e.target.closest('.inventory-slot');
    if (!targetSlot) return;

    const targetIndex = parseInt(targetSlot.dataset.slot, 10);
    targetSlot.classList.remove('drag-over');

    // Get source index from drag data
    const sourceData = e.dataTransfer.getData('application/x-inventory-slot');
    if (!sourceData) return;

    const sourceIndex = parseInt(sourceData, 10);

    if (sourceIndex === targetIndex) return;

    // Move the item
    const result = gameState.moveInventoryItem(sourceIndex, targetIndex);

    if (result.success) {
        logger.debug(`[INVENTORY] Moved slot ${sourceIndex} to ${targetIndex}`);
        // Force UI update
        forceRefreshInventory();
    } else {
        logger.debug(`[INVENTORY] Move failed: ${result.reason}`);
    }
}

/**
 * Handle drag end (cleanup)
 */
function handleDragEnd(e) {
    // Re-enable tooltips
    document.body.classList.remove('dragging');

    // Remove all drag classes
    const inventoryDiv = document.getElementById('inventory');
    if (inventoryDiv) {
        const slots = inventoryDiv.querySelectorAll('.inventory-slot');
        slots.forEach(slot => {
            slot.classList.remove('dragging', 'drag-over');
        });
    }

    dragSourceIndex = null;
}

/**
 * Handle click on inventory slot to equip or eat item
 */
function handleClick(e) {
    const slotEl = e.target.closest('.inventory-slot');
    if (!slotEl || slotEl.classList.contains('empty')) return;

    const itemId = slotEl.dataset.itemId;
    if (!itemId) return;

    const item = getItem(itemId);
    if (!item) return;

    // Check if equippable
    if (item.slot) {
        const activeSet = gameState.getActiveEquipmentSet();
        const result = gameState.equipItem(item.slot, itemId);
        if (result.success) {
            combatSkill.invalidateSetBonusCache();
            // Show which set it was equipped to if different from active
            if (result.targetSet && result.targetSet !== activeSet) {
                const setNames = { melee: 'Melee', ranged: 'Ranged', magic: 'Magic' };
                toastSuccess(`Equipped ${item.name} to ${setNames[result.targetSet]} set`);
            } else {
                toastSuccess(`Equipped ${item.name}`);
            }
            logger.debug(`[INVENTORY] Equipped ${itemId} to ${item.slot} (${result.targetSet} set)`);
            // Force full refresh for set armor (tooltips depend on equipped count)
            if (item.setId) {
                forceRefreshInventory();
            } else {
                updateInventoryDisplay();
            }
        } else {
            toastWarning(result.reason || 'Could not equip item');
        }
        return;
    }

    // Check if edible (food with healAmount)
    if (item.healAmount) {
        // Click eats food
        const result = combatSkill.eatFood(itemId);
        if (result.success) {
            toastSuccess(`Ate ${item.name} (+${result.healed} HP)`);
            logger.debug(`[INVENTORY] Ate ${itemId}, healed ${result.healed} HP`);
            updateInventoryDisplay();
        } else {
            toastWarning(result.reason || 'Could not eat');
        }
        return;
    }

    // Check if potion (consumable with effects)
    if (item.effects && item.effects.category && item.effects.duration) {
        // Shift+click stores all potions in Apothecary Belt
        if (e.shiftKey) {
            if (!gameState.isApothecaryBeltUnlocked()) {
                toastWarning('Apothecary Belt not unlocked (requires Apothecary Station)');
                return;
            }
            const quantity = gameState.getItemCount(itemId);
            const slots = gameState.getApothecarySlots();
            // Find existing slot with same potion, or first empty slot
            let targetSlot = slots.findIndex(s => s && s.itemId === itemId);
            if (targetSlot === -1) targetSlot = slots.findIndex(s => !s || s.quantity <= 0);
            if (targetSlot === -1) {
                toastWarning('Apothecary Belt is full');
                return;
            }
            const result = gameState.storeApothecaryPotion(targetSlot, itemId, quantity);
            if (result.success) {
                toastSuccess(`Stored ${quantity}x ${item.name} in Apothecary Belt`);
                logger.debug(`[INVENTORY] Stored ${quantity}x ${itemId} in Apothecary Belt`);
                updateInventoryDisplay();
            } else {
                toastWarning(result.reason || 'Could not store potion');
            }
            return;
        }

        // Regular click drinks potion
        const result = combatSkill.drinkPotion(itemId);
        if (result.success) {
            const durationMin = Math.floor(item.effects.duration / 60000);
            toastSuccess(`Drank ${item.name}! Buff active for ${durationMin} min.`);
            logger.debug(`[INVENTORY] Drank ${itemId}, buff applied`);
            updateInventoryDisplay();
        } else {
            toastWarning(result.reason || 'Could not drink potion');
        }
        return;
    }

    // Check if it's a Ritual Insight (usable XP item)
    if (isInsightItem(itemId)) {
        showInsightModal(itemId, { useAll: e.shiftKey });
        return;
    }
}

/**
 * Handle right-click on inventory slot to toggle item lock
 */
function handleContextMenu(e) {
    const slot = e.target.closest('.inventory-slot');
    if (!slot) return;

    e.preventDefault(); // Prevent browser context menu

    // Can only lock items, not empty slots
    const itemId = slot.dataset.itemId;
    if (!itemId) return;

    const slotIndex = parseInt(slot.dataset.slot, 10);
    const newLockState = gameState.toggleItemLock(itemId);
    logger.debug(`[INVENTORY] Item ${itemId} ${newLockState ? 'locked' : 'unlocked'}`);

    // Update the slot visually
    const currentSlots = gameState.getInventorySlots();
    updateSlotInPlace(slot, currentSlots[slotIndex], slotIndex);
    previousSlotState[slotIndex] = currentSlots[slotIndex] ? { ...currentSlots[slotIndex] } : null;
}

/**
 * Initialize inventory UI (called once on game start)
 * Sets up event listeners for drag and drop
 */
export function initInventoryUI() {
    // Reset state
    resetInventoryUI();

    // Initial render
    updateInventoryDisplay();

    // Setup drag and drop event listeners
    const inventoryDiv = document.getElementById('inventory');
    if (inventoryDiv) {
        // Use event delegation for drag events
        inventoryDiv.addEventListener('dragstart', handleDragStart);
        inventoryDiv.addEventListener('dragover', handleDragOver);
        inventoryDiv.addEventListener('dragleave', handleDragLeave);
        inventoryDiv.addEventListener('drop', handleDrop);
        inventoryDiv.addEventListener('dragend', handleDragEnd);

        // Right-click to toggle lock
        inventoryDiv.addEventListener('contextmenu', handleContextMenu);

        // Click to equip (if equippable)
        inventoryDiv.addEventListener('click', handleClick);

        logger.debug('[INVENTORY] Drag, drop, lock, and equip initialized');
    }
}

export default {
    renderInventoryGrid,
    updateInventoryDisplay,
    initInventoryUI,
    resetInventoryUI,
    forceRefreshInventory,
    INVENTORY_SLOTS,
    INVENTORY_COLS,
    INVENTORY_ROWS
};
